import {
  Box,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { Cell, Column, useSortBy, useTable } from 'react-table';

import { ObjectType } from '../../framework/utils/UtilityTypes';
import { useSizeContext } from '../containers/SizeProvider';

type CustomizeRenderProps<D extends object = ObjectType> = {
  cell: Omit<Cell<D>, 'getCellProps'>;
  data: D;
};

/**
 * テーブルプロパティ
 */
type StyledTableProps<D extends object> = {
  /**
   * カラム定義
   */
  columns: ReadonlyArray<Column<D>>;
  /**
   * テーブルセル
   */
  customizeRender?: React.FunctionComponent<CustomizeRenderProps<D>>;
  /**
   * データ
   */
  data: Array<D>;
};

/**
 * スタイル統一テーブル
 * @param props - プロパティ
 * @returns
 */
const StyledTable = <D extends object>({
  columns,
  customizeRender: CustomizeRender = (crProps: CustomizeRenderProps<D>) => (
    <>{crProps.cell.render('Cell')}</>
  ),
  data,
}: StyledTableProps<D>) => {
  const sizes = useSizeContext();
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable({ columns, data }, useSortBy);

  return (
    <Box
      maxWidth="calc(100vw - 56px)"
      overflowX="scroll"
      whiteSpace="nowrap"
      width="100%"
    >
      {data.length === 0 ? (
        <Text fontSize={sizes.fontSize.rawMessage}>データがありません</Text>
      ) : (
        <Table
          {...getTableProps()}
          backgroundColor="white"
          borderColor="gray.300"
          borderRadius="md"
          borderWidth={2}
          size="sm"
        >
          <Thead>
            {headerGroups.map(({ getHeaderGroupProps, headers }) => {
              const { key, ...headerGroupProps } = getHeaderGroupProps();

              return (
                <Tr {...headerGroupProps} key={key}>
                  {headers.map(
                    ({
                      getHeaderProps,
                      getSortByToggleProps,
                      isNumeric,
                      isSorted,
                      isSortedDesc,
                      render,
                    }) => {
                      const { key, ...headerProps } = getHeaderProps(
                        getSortByToggleProps()
                      );

                      return (
                        <Th {...headerProps} key={key} isNumeric={isNumeric}>
                          {render('Header')}
                          <Box as="span">
                            {isSorted ? (
                              isSortedDesc ? (
                                <Icon as={FiChevronDown} />
                              ) : (
                                <Icon as={FiChevronUp} />
                              )
                            ) : null}
                          </Box>
                        </Th>
                      );
                    }
                  )}
                </Tr>
              );
            })}
          </Thead>
          <Tbody {...getTableBodyProps()} fontSize={sizes.fontSize.listMessage}>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();

              return (
                <Tr {...rowProps} key={key}>
                  {row.cells.map(({ getCellProps, ...cell }) => {
                    const { key, ...cellProps } = getCellProps();

                    return (
                      <Td
                        {...cellProps}
                        key={key}
                        isNumeric={cell.column.isNumeric}
                      >
                        <CustomizeRender cell={cell} data={row.original} />
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};

export default StyledTable;
